import React, { useState, useContext, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import roomContext from "../context/rooms/roomContext";
import "../styles/Confirm.css";

const ConfirmBooking = (props) => {
  const confirmContext = useContext(roomContext);
  const {
    numberOfRooms,
    numberOfGuest,
    name,
    age,
    aadhar,
    mobileNumber,
    email,
    address,
    checkInDate,
    checkOutDate,
    availableRooms,
  } = confirmContext;

  const { country, state, district, city, pincode } = address;
  const { type, amount, close, inProgress, setInProgress, resetForm } = props;
  const navigate = useNavigate();

  const [message, setMessage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [statusModal, setStatusModal] = useState(false);
  const [totalAmount, setTotalAmount] = useState(amount);
  const [paymentPercentage, setPaymentPercentage] = useState(60);
  const [userId, setUserId] = useState(null);
  const [countdown, setCountdown] = useState(10);
  const ws = useRef(null);
  const isMounted = useRef(true);
  const razorpay = useRef(null);

  const payableAmount = Math.ceil((paymentPercentage / 100) * totalAmount);
  const pendingAmount = totalAmount - payableAmount;

  useEffect(() => {
    if (message.includes("Redirecting")) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [message]);

  useEffect(() => {
    isMounted.current = true;

    const connect = () => {
      const protocol = window.location.protocol === "https:" ? "wss" : "ws";

      ws.current = new WebSocket(`${protocol}://${window.location.host}/ws/`);

      ws.current.onmessage = (event) => {
        const data = JSON.parse(event.data);
        switch (data.type) {
          case "CONNECTION_INIT":
            setUserId(data.userId);
            localStorage.setItem("UserId", data.userId);
            break;

          case "QUEUE_UPDATE":
            setMessage(data.message);
            setStatusModal(true);
            break;

          case "PAYMENT_TIMEOUT":
            closeRazorpayWindow();
            unloadRazorpayScript();
            setMessage(data.message);
            setStatusModal(true);
            localStorage.removeItem("UserId");
            setTimeout(() => (window.location.href = "/"), 10000);
            break;
          default:
            // No action needed for unknown types
            break;
        }
      };

      ws.current.onclose = () => {
        if (isMounted.current) {
          setTimeout(() => {
            if (isMounted.current) {
              connect();
            }
          }, 5000);
        }
      };
    };

    connect();

    // Check for existing user ID
    const savedUserId = localStorage.getItem("UserId");
    if (savedUserId) {
      setUserId(savedUserId);
    }

    return () => {
      isMounted.current = false;
      if (ws.current) {
        ws.current.close();
      }
    };
  }, []); // Empty dependency array

  useEffect(() => {
    const calculateTotalAmount = () => {
      if (!checkInDate || !checkOutDate) {
        setTotalAmount(amount);
        return;
      }

      const checkIn = new Date(checkInDate);
      const checkOut = new Date(checkOutDate);

      if (checkOut <= checkIn) {
        setTotalAmount(amount);
        return;
      }

      const timeDifference = checkOut.getTime() - checkIn.getTime();
      const nights = Math.ceil(timeDifference / (1000 * 3600 * 24));

      setTotalAmount(nights * amount * numberOfRooms);
    };

    calculateTotalAmount();
  }, [checkInDate, checkOutDate, amount, numberOfRooms]);

  // Dedicated function to close Razorpay
  const closeRazorpayWindow = () => {
    try {
      // Standard close
      if (razorpay.current?.close) {
        razorpay.current.close();
      }

      // Gentle DOM cleanup
      const rzpIframe = document.querySelector('iframe[src*="razorpay"]');
      if (rzpIframe) {
        rzpIframe.style.opacity = "0";
        setTimeout(() => rzpIframe.remove(), 300);
      }

      const razorpayModal = document.querySelector(".razorpay-container");
      if (razorpayModal) {
        razorpayModal.style.opacity = "0";
        setTimeout(() => {
          razorpayModal.remove();
        }, 300);
      }

      razorpay.current = null; // Reset reference
    } catch (error) {
      console.error("Error closing Razorpay:", error);
    }
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve, reject) => {
      // Check if already loaded with a small delay to ensure full initialization
      if (window.Razorpay && typeof window.Razorpay === "function") {
        return resolve(true);
      }

      setMessage(
        "Checking for your booking status in the queue. Please wait...",
      );

      // Remove any existing script first to avoid duplicates
      document
        .querySelectorAll('script[src*="razorpay"]')
        .forEach((script) => script.remove());

      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.id = "razorpay-checkout-js";
      script.async = true;
      script.onload = () => {
        // Add small delay to ensure Razorpay is fully initialized
        setTimeout(() => {
          if (window.Razorpay && typeof window.Razorpay === "function") {
            resolve(true);
          } else {
            console.error("Razorpay loaded but not properly initialized");
            reject(new Error("Razorpay not initialized"));
          }
        }, 300);
      };
      script.onerror = () => {
        console.error("Failed to load Razorpay script");
        reject(new Error("Failed to load Razorpay"));
      };
      document.body.appendChild(script);
    });
  };

  const unloadRazorpayScript = () => {
    // Remove script tag
    document.querySelectorAll('script[src*="razorpay"]').forEach((script) => {
      script.remove();
    });

    // Clear Razorpay from global scope
    delete window.Razorpay;
  };

  const handlePayment = async () => {
    if (inProgress) return;

    setInProgress(true);
    setStatusModal(true);
    closeRazorpayWindow();

    try {
      // Load Razorpay script dynamically
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        throw new Error("Failed to load payment processor");
      }
      const response = await fetch(
        `${process.env.REACT_APP_URL_SCHEME}${process.env.REACT_APP_IP_ADDRESS}/api/payment/checkout`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            paidAmount: payableAmount,
            type,
            numberOfRooms,
            checkInDate,
            checkOutDate,
            userId,
          }),
        },
      );

      const data = await response.json();
      const { payment } = data;

      if (data?.success) {
        if (!window.Razorpay || typeof window.Razorpay !== "function") {
          throw new Error("Razorpay not properly initialized");
        }
        // Store the Razorpay instance reference
        const options = {
          key: process.env.REACT_APP_RAZORPAY_KEY,
          amount: payment.amount,
          currency: payment.currency,
          name: "Hotel Shyam Palace",
          description: "Room Booking Payment",
          order_id: payment.id,

          handler: async function (response) {
            try {
              const verifyResponse = await fetch(
                `${process.env.REACT_APP_URL_SCHEME}${process.env.REACT_APP_IP_ADDRESS}/api/payment/verify-payment`,
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    razorpay_order_id: response.razorpay_order_id,
                    razorpay_payment_id: response.razorpay_payment_id,
                    razorpay_signature: response.razorpay_signature,
                    paidAmount: payableAmount,
                    pendingAmount,
                    type,
                    numberOfRooms,
                    numberOfGuest,
                    name,
                    age,
                    aadhar,
                    address,
                    mobileNumber,
                    email,
                    checkInDate,
                    checkOutDate,
                  }),
                },
              );

              const verifyData = await verifyResponse.json();
              if (verifyData.success) {
                setStatusModal(false);
                setShowModal(true);
                setInProgress(true);
                localStorage.removeItem("UserId");
                unloadRazorpayScript(); // Unload after successful verification

                const sendResponse = await fetch(
                  `${process.env.REACT_APP_URL_SCHEME}${process.env.REACT_APP_IP_ADDRESS}/api/sendbookingconfirmation`,
                  {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                      email: email,
                      bookingDetails: {
                        type,
                        numberOfRooms,
                        numberOfGuest,
                        mobileNumber,
                        guest: name.map((a, index) => ({
                          name: a,
                          age: Number(age[index]),
                          aadhar: aadhar[index],
                        })),
                        checkInDate,
                        checkOutDate,
                        paidAmount: payableAmount,
                        pendingAmount: pendingAmount,
                        orderId: response.razorpay_order_id,
                        paymentId: response.razorpay_payment_id,
                      },
                    }),
                  },
                );

                const sentEmail = await sendResponse.json();
                setMessage(
                  sentEmail.success
                    ? `An email has been sent to ${email}.\n Thank you for choosing us. We eagerly await your arrival.`
                    : "Failed to send confirmation.",
                );
                setInProgress(false);
              } else {
                setMessage(
                  "Payment verification failed. Please contact support.",
                );
                setInProgress(false);
                unloadRazorpayScript(); // Unload on verification failure
              }
            } catch (error) {
              console.error("Payment verification error:", error);
              setMessage(
                "An error occurred during payment verification. Please try again ",
              );
              setInProgress(false);
              unloadRazorpayScript();
            }
          },
          theme: {
            color: "#121212",
          },
          method: {
            card: true,
            netbanking: true,
            wallet: true,
            upi: true,
            emi: false,
          },
          modal: {
            escape: false,
            ondismiss: () => {
              setMessage("Payment window closed");
              setStatusModal(true);
              setInProgress(false);
              unloadRazorpayScript();
            },
          },
        };

        razorpay.current = new window.Razorpay(options);
        razorpay.current.open();
        setStatusModal(false);
      } else {
        if (data?.message === "NoRooms") {
          setMessage(
            `All rooms are booked for ${checkInDate} and ${checkOutDate}. You may check for other dates. Redirecting to home`,
          );
          setStatusModal(true);
          setInProgress(false);
          unloadRazorpayScript();
          setTimeout(() => (window.location.href = "/"), 10000);
        } else {
          setMessage(data.error);
          setInProgress(false);
          unloadRazorpayScript();
        }
      }
    } catch (error) {
      console.error("Payment error:", error);
      setMessage("An error occurred while initiating payment");
      setInProgress(false);
      unloadRazorpayScript();
    }
  };

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      if (razorpay.current?.close) {
        razorpay.current.close();
      }
      if (ws.current) {
        ws.current.close();
      }
    };
  }, []);

  return (
    <div className="confirm-overlay">
      <button className="close-btn" onClick={() => close()}>
        &times;
      </button>
      <h6>Confirm Booking Details</h6>
      <h5>Booking for {type}</h5>
      <h3>Make sure the info that you've provided is correct.</h3>
      <div className="confirm-container">
        <p>Number of Rooms : {numberOfRooms}</p>
        <p>Number of Guest : {numberOfGuest}</p>
        <p>Mobile Number : {mobileNumber}</p>
        <p>Email : {email}</p>
        {name.map((name, index) => (
          <div className="details" key={index}>
            <p>
              Name of Guest {index + 1} : {name}
            </p>
            <p>
              Age of Guest {index + 1} : {age[index]}
            </p>
            <p>
              Aadhar of Guest {index + 1} : {aadhar[index]}
            </p>
          </div>
        ))}
        <p>Country : {country}</p>
        <p>State : {state}</p>
        <p>District : {district}</p>
        <p>City : {city}</p>
        <p>Pincode : {pincode}</p>
        <p>Check-In-Date : {checkInDate}</p>
        <p>Check-Out-Date : {checkOutDate}</p>
      </div>
      {showModal && (
        <div className="modal">
          <div className="modal-content">
            <button
              className="close-btn"
              onClick={() => {
                setShowModal(false);
                resetForm();
                close(setInProgress(false));
                navigate("/");
              }}
            >
              &times;
            </button>
            {inProgress ? (
              <div className="loading-container">
                <h5>Confirming Booking.....</h5>
                <div className="spinner"></div>
              </div>
            ) : (
              <h5>Booking Confirmed.</h5>
            )}

            {inProgress ? (
              <p>This may take a while to confirm your booking.</p>
            ) : (
              <h3>{message}</h3>
            )}
          </div>
        </div>
      )}
      {statusModal && (
        <div className="modal">
          <div className="modal-content">
            <h3>{message}</h3>
            {message.includes("error") && (
              <button
                button
                type="submit"
                className="confirm-btn"
                onClick={handlePayment}
                disabled={inProgress || availableRooms === 0}
              >
                Try Again
              </button>
            )}
            {message.includes("Redirecting") && (
              <h3>in {countdown} seconds...</h3>
            )}
            {message.includes("front") && (
              <div className="loading-container">
                <h3>Initializing Payment</h3>
                <div className="spinner"></div>
              </div>
            )}
          </div>
        </div>
      )}
      <div className="payment-options">
        <label className="payment-option">
          <input
            type="radio"
            value={60}
            checked={paymentPercentage === 60}
            onChange={() => setPaymentPercentage(60)}
          />
          Pay 60% (₹{Math.ceil((60 / 100) * totalAmount)})
        </label>
        <label className="payment-option">
          <input
            type="radio"
            value={100}
            checked={paymentPercentage === 100}
            onChange={() => setPaymentPercentage(100)}
          />
          Pay 100% (₹ {totalAmount})
        </label>
      </div>

      <button
        type="submit"
        className="confirm-btn"
        onClick={handlePayment}
        disabled={inProgress || availableRooms === 0}
      >
        {inProgress
          ? "Processing..."
          : `Proceed to pay ₹${Math.ceil((paymentPercentage / 100) * totalAmount)}`}
      </button>
    </div>
  );
};

export default ConfirmBooking;
